import { Link as AmplifyLink, Button, Card, Flex, Text } from '@aws-amplify/ui-react';
import { EduleadError } from 'edulead';
import { useCallback, useContext, useEffect, useState } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { Link, useNavigate, useParams } from 'react-router-dom';
import EmailAddress from '../custom-components/EmailAddress';
import LoadingBars from '../custom-components/LoadingBars';
import NA from '../custom-components/NA';
import UniversalTag from '../custom-components/UniversalTag';
import { GlobalContext, GlobalContextProps } from '../global-context';
import { toastV4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import { isKeystoneVersion } from '../utils/Constants';
import General from '../utils/GeneralUtils';
import { KEYSTONE_VERSION, toLegacyPerson } from '../utils/legacyPerson';
import { handleNewExport } from '../utils/newExportFeature';
import { PersonDetailKeystone } from './person/PersonDetailKeystone';
import './Person.scss';

export function Person(props: any) {

  const globalContext = useContext(GlobalContext);

  const { id } = useParams()
  const navigate = useNavigate();
  const [uid] = useState(id);
  const [response, setResponse] = useState<any>(null);
  const [version, setVersion] = useState<number | undefined>(undefined);
  const [isLoadingContact, setIsLoadingContact] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);

  // Handler for export button - exports this specific contact
  const handleExport = useCallback(async () => {
    setIsExportLoading(true);
    try {
      const exportId = await handleNewExport({
        type: 'selected',
        ids: [uid as string],
        entity: 'CONTACT',
        searchParams: {},
        estimatedCount: 1,
      });
      navigate(`/exports/${exportId}/target`);
    } finally {
      setIsExportLoading(false);
    }
  }, [uid, navigate]);

  async function loadContacts() {
    try {
      setIsLoadingContact(true)
      const got = await eduleadV4.people.get({ id: uid as string })
      const data = got.person ? toLegacyPerson(got.person) : null

      if (!data) {
        navigate('/404', { replace: true });
        return;
      }

      setVersion(KEYSTONE_VERSION);
      setResponse({ data }); // the response structure the page kept before the Go API
      const displayName = data.name || [data.firstName, data.lastName].filter(Boolean).join(' ');
      General.setTitle(document, displayName, globalContext);
    } catch (err) {
      // An unknown person, or an id that is no person's id (the API validates it as a UUID):
      // legacy answered both with a 404.
      if (err instanceof EduleadError && (err.statusCode === 404 || err.statusCode === 400)) {
        navigate('/404', { replace: true });
        return;
      }
      toastV4Error(err, 'Failed to load contact')
    } finally {
      setIsLoadingContact(false)
    }
  }

  useEffect(() => {
    loadContacts();
  }, [props.id]);

  // Set page title based on person data
  useEffect(() => {
    const displayName = response?.data?.name || [response?.data?.firstName, response?.data?.lastName].filter(Boolean).join(' ');
    General.setTitle(document, displayName || 'Person', globalContext);
  }, [response, globalContext]);

  // Loading state - single loading bar for all versions
  if (!response) {
    return (
      <div className="content content-800">
        <LoadingBars style={{ margin: '20px 0' }} numberOfRows={6} />
      </div>
    );
  }

  // Keystone version: render new component
  if (isKeystoneVersion(version)) {
    return (
      <div className="content plain">
        <PersonDetailKeystone
          person={response.data}
          onExport={handleExport}
          isExportLoading={isExportLoading}
        />
      </div>
    );
  }

  // Legacy version: render original view
  return <GlobalContext.Consumer>
    {
      (globalContextProps: GlobalContextProps) => <>
        <div className="content content-800">
          <Flex alignItems="center" justifyContent="space-between" className="contact__header">
            <Flex alignItems="center" gap="10px">
              <div className={`avatar avatar-${General.getCSSFromId(response.data.id)}`}>
                { General.getInitials(response.data?.name) }
              </div>
              <h2 className="contact__title">{ response.data?.name || '<Contact\'s name not available>' }</h2>
            </Flex>
            <Button
              onClick={handleExport}
              size="small"
              className="tiny"
              isLoading={isExportLoading}
              isDisabled={isExportLoading}
            >
              Export
            </Button>
          </Flex>
          <Card variation='outlined' className="contact__card">
            {
              !response?.data ?
                <div className="contact__no-data">No contact found</div> :
                <>
                  <div className="contact__row"><strong className="contact__label">Job title: </strong>{ response.data['title'] ? <>{ response.data['title'] }</> : <NA /> }</div>
                  <div className="contact__row"><strong className="contact__label">Email: </strong><EmailAddress email={response.data['email']} emailDomainCount={response.data['emailDomainCount']} emailVerificationStatus={response.data['emailVerificationStatus']} emailLastVerifiedAt={response.data['emailLastVerifiedAt']} /></div>
                  <div><strong className="contact__label">Phone: </strong>{ response.data['phone'] || <NA /> }</div>
                  {
                    response.data?.['bios'] && <div>
                      {
                        response.data['bios'].map((bio: any) => {
                          return <div key={bio?.created_at} className="contact__bio-container">
                            { bio?.bio }
                          </div>
                        })
                      }
                    </div>
                  }
                </>
            }
          </Card>
          {response?.data && (
            <div style={{ marginTop: 20 }}>
              <Text
                variation="primary"
                as="p"
                fontWeight={400}
                fontStyle="italic"
                fontSize='small'
              >
                <strong>{ response.data?.name || 'Contact' }</strong> is associated with { response.data?.belongsTo?.length || 'these' } institution{ response.data?.belongsTo?.length === 1 ? '' : 's' }
              </Text>
              <Card variation='outlined' style={{fontSize: 14, marginTop: 5}}>
                {
                  !response.data?.belongsTo?.length ?
                    <></> :
                    <Flex direction='column' gap={10}>
                      {
                        response.data.belongsTo
                          ?.map((item: any) => item?.institution)
                          ?.map((item: any) =>
                            <div key={item?.id}>
                              <UniversalTag category={item?.type} /> <Link to={`/directory/institutions/${item?.id}`}>{ item?.name }</Link>, { General.getStateName(item?.loc_state) }
                            </div>
                          )
                      }
                    </Flex>
                }
              </Card>
            </div>
          )}
          {response?.data?.foundOnUrls?.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <Text
                variation="primary"
                as="p"
                fontWeight={400}
                fontStyle="italic"
                fontSize='small'
              >
                <strong>{ response.data?.name || 'Contact' }</strong> was found on { response.data?.foundOnUrls?.length || 'these' } URL{ response.data?.foundOnUrls?.length === 1 ? '' : 's' }
              </Text>
              <Card variation='outlined' style={{fontSize: 14, marginTop: 5}} >
                <Flex direction='column' gap={10}>
                  {
                    response.data.foundOnUrls.map((item: any) =>
                      <div key={item.url}>
                        <AmplifyLink
                          target='_blank'
                          href={ item?.url }
                        >
                          { General.replaceMiddleWithDots(item?.url, 60, 25) } <FaExternalLinkAlt size={11} />
                        </AmplifyLink>{ item?.domain?.engine && <span data-tooltip-id="main-tooltip" data-tooltip-content={`This website is powered by ${item?.domain?.engine}`}>{ item?.domain?.engine }</span> }
                      </div>
                    )
                  }
                </Flex>
              </Card>
            </div>
          )}
        </div>
      </>
    }
  </GlobalContext.Consumer>
}
